import { Plus, AlertCircle, ArrowLeftRight, Hand, Timer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import type { ActionType, PlaybackRoute } from '@/types'
import { ENABLE_WAIT_STEP } from '@/config/scenarioConfig'
import Button from '@/components/common/Button'
import FloatingPanel from './FloatingPanel'
import RouteList from './RouteList'
import WaitSecondsInput from './WaitSecondsInput'

interface ComposerRoutePanelProps {
  s: ReturnType<typeof useScenarios>
  pos: { x: number; y: number }
  onMove: (pos: { x: number; y: number }) => void
  height: number
  onResize: (height: number) => void
}

// シナリオ編集・新規作成モードのルート選択フローティングパネル。
// 追加候補一覧と、プレビュー中ルートのアクション / 向きの設定・追加を担う。
export default function ComposerRoutePanel({
  s,
  pos,
  onMove,
  height,
  onResize,
}: ComposerRoutePanelProps) {
  const { t } = useTranslation()
  const enableCargoToggle = s.enableCargoToggle

  return (
    <FloatingPanel
      title={t('scenarios.addRoute')}
      pos={pos}
      onMove={onMove}
      width="w-[28rem]"
      height={height}
      onResize={onResize}
      minHeight={260}
    >
      <div className="h-full flex flex-col min-h-0">
        {/* ルート選択（追加候補一覧） */}
        <div className="flex-1 overflow-y-auto p-2">
          {/* 停止手順（秒数指定）・一時停止手順（タップで再開）の追加
              （ルートと同じく 選択 → 設定 → シナリオに追加）。
              停止位置は直前手順の終点。先頭に置くと出発位置で停止する。
              ENABLE_WAIT_STEP=false ではカードを出さない */}
          {ENABLE_WAIT_STEP && (
            <>
              <button
                type="button"
                onClick={s.handleSelectWait}
                className={`w-full mb-1 flex items-center gap-2 p-1.5 rounded-lg border text-left transition-colors ${
                  s.previewWait
                    ? 'bg-orange-50 border-orange-400'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="h-10 w-12 flex-shrink-0 border border-gray-200 rounded bg-purple-50 flex items-center justify-center">
                  <Timer size={16} className="text-purple-500" />
                </div>
                <div className="flex-1 min-w-0 text-xs font-medium text-gray-700">
                  {t('scenarios.addWait')}
                </div>
              </button>
              <button
                type="button"
                onClick={s.handleSelectWaitResume}
                className={`w-full mb-1 flex items-center gap-2 p-1.5 rounded-lg border text-left transition-colors ${
                  s.previewWaitResume
                    ? 'bg-orange-50 border-orange-400'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="h-10 w-12 flex-shrink-0 border border-gray-200 rounded bg-purple-50 flex items-center justify-center">
                  <Hand size={16} className="text-purple-500" />
                </div>
                <div className="flex-1 min-w-0 text-xs font-medium text-gray-700">
                  {t('scenarios.addWaitResume')}
                </div>
              </button>
            </>
          )}
          <RouteList
            map={s.map}
            routes={s.sortedRoutes}
            thumbnails={s.thumbnails}
            isSelected={(route) => s.previewRoute?.id === route.id}
            onSelectRoute={s.handleSelectRoute}
            isRouteDisabled={(route: PlaybackRoute) =>
              !s.isRouteConnectable(route)
            }
            renderUsageBadge={(route: PlaybackRoute) => {
              const currentPositions = s.currentScenarioRouteMapIds.get(
                route.mapId
              )
              if (currentPositions) {
                return (
                  <span className="flex-shrink-0 bg-blue-500 text-white text-[10px] font-bold leading-none px-1 py-0.5 rounded">
                    {t('scenarios.usedCurrent', {
                      positions: currentPositions.join(','),
                    })}
                  </span>
                )
              }
              if (s.otherScenarioRouteMapIds.has(route.mapId)) {
                return (
                  <span className="flex-shrink-0 bg-gray-500 text-white text-[10px] font-bold leading-none px-1 py-0.5 rounded">
                    {t('scenarios.usedOther')}
                  </span>
                )
              }
              return null
            }}
          />
        </div>

        {/* 設定パネル（プレビュー中のみ） */}
        {s.previewRoute && (
          <div className="p-2 border-t bg-gray-50">
            {s.distanceInfo && s.cart.length > 0 && !s.distanceInfo.isValid && (
              <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-1.5">
                <AlertCircle size={14} className="flex-shrink-0" />
                {t('scenarios.distanceErrorPreview')}
              </div>
            )}
            {enableCargoToggle && !s.isCargoStartValid && (
              <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-1.5">
                <AlertCircle size={14} className="flex-shrink-0" />
                {t('scenarios.cargoStartErrorPreview')}
              </div>
            )}
            <div className="flex gap-1.5 mb-2">
              {(['move', 'move_loaded', 'pickup', 'place'] as ActionType[]).map(
                (action) => {
                  // 荷積み・荷下ろしのみ高さ保存マーク終点を要求する。
                  // 荷物を持っている間は「移動のみ」「荷積み」不可、
                  // 持っていない間は「荷物あり移動」「荷下ろし」不可。
                  const isDisabled =
                    (enableCargoToggle &&
                      (action === 'pickup' || action === 'place') &&
                      !s.isEndSlotCargo) ||
                    (action === 'move' && s.isMoveDisabled) ||
                    (action === 'move_loaded' && s.isMoveLoadedDisabled) ||
                    (action === 'pickup' && s.isPickupDisabled) ||
                    (action === 'place' && s.isPlaceDisabled)
                  return (
                    <label
                      key={action}
                      className={`flex-1 flex items-center justify-center text-center whitespace-nowrap py-1.5 px-2 rounded text-xs transition-colors border ${
                        isDisabled
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : s.previewAction === action
                            ? 'border-blue-400 border-2 bg-blue-100 cursor-pointer'
                            : 'bg-white border hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <input
                        type="radio"
                        name="previewAction"
                        value={action}
                        checked={s.previewAction === action}
                        disabled={isDisabled}
                        onChange={() => s.handlePreviewActionChange(action)}
                        className="sr-only"
                      />
                      {action === 'move'
                        ? t('scenarios.moveOnly')
                        : action === 'move_loaded'
                          ? t('scenarios.moveLoaded')
                          : action === 'pickup'
                            ? t('scenarios.pickup')
                            : t('scenarios.place')}
                    </label>
                  )
                }
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                onClick={s.handleSwapDirection}
                variant="blue"
                size="sm"
                className="flex items-center justify-center gap-1 flex-1"
              >
                <ArrowLeftRight size={14} />
                {t('scenarios.swapDirection')}
              </Button>
              <Button
                onClick={s.handleConfirmAdd}
                disabled={s.isAddDisabled}
                variant="blue"
                size="sm"
                className="flex items-center justify-center gap-1 flex-1"
              >
                <Plus size={16} />
                {t('scenarios.addToScenario')}
              </Button>
            </div>
          </div>
        )}

        {/* 停止手順の設定パネル（停止カード選択中のみ） */}
        {ENABLE_WAIT_STEP && s.previewWait && (
          <div className="p-2 border-t bg-gray-50">
            <WaitSecondsInput
              value={s.previewWaitSec}
              onChange={s.handlePreviewWaitSecChange}
              className="mb-2"
            />
            <Button
              onClick={s.handleConfirmAddWait}
              disabled={s.isAddWaitDisabled}
              variant="blue"
              size="sm"
              className="flex items-center justify-center gap-1 w-full"
            >
              <Plus size={16} />
              {t('scenarios.addToScenario')}
            </Button>
          </div>
        )}

        {/* 一時停止手順（タップで再開）の設定パネル（カード選択中のみ） */}
        {ENABLE_WAIT_STEP && s.previewWaitResume && (
          <div className="p-2 border-t bg-gray-50">
            <div className="mb-2 flex items-start gap-2 text-xs text-gray-700">
              <Hand
                size={14}
                className="mt-0.5 flex-shrink-0 text-purple-500"
              />
              <div>{t('scenarios.waitResumeHint')}</div>
            </div>
            <Button
              onClick={s.handleConfirmAddWaitResume}
              variant="blue"
              size="sm"
              className="flex items-center justify-center gap-1 w-full"
            >
              <Plus size={16} />
              {t('scenarios.addToScenario')}
            </Button>
          </div>
        )}
      </div>
    </FloatingPanel>
  )
}
